import type {
  MenuAvailabilityCategory,
  MenuAvailabilityChange,
  MenuAvailabilityItem,
  SnoozeDuration,
} from '../../hooks/useMenuAvailabilityQuery';

export interface StagedChange extends MenuAvailabilityChange {
  label: string;
}

export type AvailabilityFilter = 'all' | 'available' | 'snoozed';

export const durationOptions: Array<{
  value: SnoozeDuration;
  label: string;
}> = [
  { value: 'until_close', label: 'Until close' },
  { value: '30_minutes', label: '30 minutes' },
  { value: '1_hour', label: '1 hour' },
  { value: '2_hours', label: '2 hours' },
  { value: 'custom', label: 'Custom date/time' },
];

export const FILTER_OPTIONS: Array<{
  value: AvailabilityFilter;
  label: string;
}> = [
  { value: 'all', label: 'All items' },
  { value: 'available', label: 'Available' },
  { value: 'snoozed', label: 'Snoozed' },
];

export function formatPrice(price: number) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
  }).format(price / 100);
}

export function formatDateTime(value: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export function toDateTimeLocal(value: Date) {
  const offset = value.getTimezoneOffset() * 60_000;
  return new Date(value.getTime() - offset).toISOString().slice(0, 16);
}

export function getPresetEnd(
  duration: SnoozeDuration,
  nextClosingTime: string | null,
  customValue: string,
) {
  if (duration === 'until_close') return nextClosingTime;
  if (duration === 'custom') {
    return customValue ? new Date(customValue).toISOString() : null;
  }
  const minutes = {
    '30_minutes': 30,
    '1_hour': 60,
    '2_hours': 120,
  }[duration];
  return new Date(Date.now() + minutes * 60_000).toISOString();
}

/** Staged state wins over the server's until the change is saved. */
export function effectiveSnoozed(
  item: MenuAvailabilityItem,
  staged: StagedChange | undefined,
) {
  return staged ? staged.action === 'snooze' : item.is_snoozed;
}

export function filterCategories(
  categories: MenuAvailabilityCategory[],
  changes: Record<number, StagedChange>,
  search: string,
  filter: AvailabilityFilter,
) {
  const term = search.trim().toLocaleLowerCase();
  return categories
    .map((category) => ({
      ...category,
      items: category.items.filter((item) => {
        const isSnoozed = effectiveSnoozed(item, changes[item.id]);
        const matchesSearch =
          !term ||
          item.name.toLocaleLowerCase().includes(term) ||
          category.name.toLocaleLowerCase().includes(term);
        const matchesFilter =
          filter === 'all' || (filter === 'snoozed' ? isSnoozed : !isSnoozed);
        return matchesSearch && matchesFilter;
      }),
    }))
    .filter((category) => category.items.length > 0);
}
